import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CircularIconContainer,
  Form,
  FormField,
  FormFieldError,
  FormFieldItem,
  IconNotificationText,
  IconPlus,
} from '@/components';
import { MarkdownEditor } from '@/components/MarkdownEditor/MarkdownEditor';
import {
  useIsTemplateInterview,
  useShareToken,
} from '@/features/admin-interview';

import {
  useAddInformationalTextMutation,
  useAddTemplateToInterviewMutation,
  useGetInterviewQuery,
} from './useAdminInterviewQueries';

interface EditInformationalTextModalProps extends Omit<ModalProps, 'children'> {
  interviewId: string;
}

type FormInputs = {
  informationText: string;
};

export const EditInformationalTextModal: React.FC<
  EditInformationalTextModalProps
> = ({ interviewId, ...modalProps }) => {
  const { onClose } = modalProps;
  const toast = useToast();
  const isTemplate = useIsTemplateInterview();
  const shareToken = useShareToken();
  const { t } = useTranslation(['common', 'candidate', 'interview']);
  const getInterviewQuery = useGetInterviewQuery(
    {
      interviewsId: interviewId!,
    },
    {
      axiosRequestConfig: {
        overrideSecurityToken: shareToken,
      },
    }
  );

  const addInformationalTextMutation = useAddInformationalTextMutation({
    onSuccess() {
      form.reset();
      onClose?.();
    },
    onError() {
      toast({
        title: t('candidate:inviteNewCandidate.addCandidateError'),
        status: 'error',
      });
    },
  });

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: {
      informationText: '',
    },
    values: {
      informationText:
        getInterviewQuery.data?.interview?.informationalText ?? '',
    },
  });

  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (isTemplate) await createInterviewFromTemplate();
    addInformationalTextMutation.mutateAsync({
      interviewId,
      body: {
        informationalText: data.informationText,
      },
    });
  };

  return (
    <Modal
      {...modalProps}
      onClose={() => {
        form.reset();
        onClose?.();
      }}
    >
      <ModalOverlay />
      <ModalContent maxW="40rem" borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconNotificationText} />
            <Text>
              {t('interview:createNewInterview.directiveText.stepTitle')}
            </Text>
            <Text fontSize="sm" fontWeight="light" textAlign="center">
              {t('interview:createNewInterview.directiveText.description')}
            </Text>
          </VStack>
        </ModalHeader>

        <ModalBody w="full" pb={2}>
          <Form {...form} onSubmit={onSubmit}>
            <Stack p={4} rounded="lg">
              <FormField
                control={form.control}
                name="informationText"
                type="custom"
                rules={{
                  minLength: {
                    value: 20,
                    message: t(
                      'interview:createNewInterview.directiveText.directiveTextMinLength'
                    ),
                  },
                }}
                render={({ field: { value, ref, onBlur, onChange, name } }) => {
                  return (
                    <FormFieldItem>
                      <Box
                        borderRadius="lg"
                        border="1px"
                        borderColor="gray.200"
                        background="white"
                        id={name}
                      >
                        <MarkdownEditor
                          placeholder={t(
                            'interview:createNewInterview.directiveText.directiveTextPlaceholder'
                          )}
                          markdown={value ?? ''}
                          onBlur={onBlur}
                          ref={ref}
                          onChange={onChange}
                        />
                      </Box>
                      <FormFieldError />
                    </FormFieldItem>
                  );
                }}
              />

              <Button
                type="submit"
                mb={8}
                mt={4}
                isLoading={addInformationalTextMutation.isPending}
                isDisabled={!form.formState.isDirty}
                leftIcon={<IconPlus />}
                variant="@primary"
                w="full"
              >
                {t('common:actions.save')}
              </Button>
            </Stack>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
